import { Link } from 'react-router-dom'
import type { CSSProperties } from 'react'
import { useAdminActivity } from '../hooks/useAdminActivity'
import { useLocale } from '../i18n/context'
import type { UserActivityRow, WorstCard, CohortSummary } from '../types'

// ── Mastery ladder mini-bar ────────────────────────────────────────────────────
// Single-hue magnitude encoding (DESIGN_SYSTEM Wave 3): the accent hue, deepening
// opacity as the level rises. Segment width is proportional to the card count at
// that level. No rainbow — the retired per-level hue scale is not used.

function MasteryLadder({ ladder }: { ladder: number[] }) {
  const total = ladder.reduce((a, b) => a + b, 0)
  if (total === 0) {
    return <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>—</span>
  }
  return (
    <div
      style={{ display: 'flex', width: 140, height: 16, borderRadius: 4, overflow: 'hidden', backgroundColor: 'var(--bg-secondary)' }}
      title={ladder.map((c, i) => `L${i + 1}: ${c}`).join('  ')}
    >
      {ladder.map((count, i) =>
        count > 0 ? (
          <div
            key={i}
            style={{
              flex: count,
              // Opacity ramp 0.28 → 1.0 across levels 1 → 8.
              backgroundColor: 'var(--accent)',
              opacity: 0.28 + (i / 7) * 0.72,
            }}
          />
        ) : null,
      )}
    </div>
  )
}

// ── Stat card ───────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, highlight }: { label: string; value: string | number; sub?: string; highlight?: boolean }) {
  return (
    <div style={{
      backgroundColor: 'var(--bg-surface)',
      border: `1px solid ${highlight ? 'var(--accent)' : 'var(--border)'}`,
      borderRadius: 12,
      padding: '18px 22px',
      minWidth: 130,
      flex: '1 1 130px',
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: highlight ? 'var(--accent)' : 'var(--text-primary)', lineHeight: 1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>{sub}</div>}
    </div>
  )
}

// ── Cohort summary section ──────────────────────────────────────────────────────

function CohortSection({ cohort }: { cohort: CohortSummary }) {
  const { tr } = useLocale()
  const t = tr.adminActivity
  const abandonPct = cohort.abandonment_rate !== null ? `${(cohort.abandonment_rate * 100).toFixed(0)}%` : '—'
  const maxCohort = Math.max(...cohort.retention.map((b) => b.cohort_size), 1)

  return (
    <>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 28 }}>
        <StatCard label={t.totalUsers} value={cohort.total_users} />
        <StatCard label={t.activeThisWeek} value={cohort.active_this_week} sub={t.activeWindow(cohort.active_window_days)} highlight />
        <StatCard label={t.sessionsStarted} value={cohort.sessions_started.toLocaleString()} />
        <StatCard label={t.sessionsDone} value={cohort.sessions_completed.toLocaleString()} />
        <StatCard label={t.abandonRate} value={abandonPct} sub={`${cohort.sessions_abandoned.toLocaleString()}`} />
      </div>

      <h3 style={subHeadingStyle}>{t.retentionTitle}</h3>
      <div style={tableWrapStyle}>
        <table style={tableStyle}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={thStyle}>{t.cohortCol}</th>
              <th style={thStyle}>{t.cohortSize}</th>
              <th style={{ ...thStyle, width: '55%' }}>{t.activeCol}</th>
            </tr>
          </thead>
          <tbody>
            {cohort.retention.map((b, i) => (
              <tr key={b.bucket} style={{ borderBottom: i < cohort.retention.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <td style={tdStyle}>{b.bucket}</td>
                <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--text-primary)' }}>{b.cohort_size}</td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1, height: 16, backgroundColor: 'var(--bg-secondary)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: `${(b.cohort_size / maxCohort) * 100}%`,
                        backgroundColor: 'var(--accent)',
                        opacity: 0.35,
                      }}>
                        <div style={{
                          height: '100%',
                          width: b.cohort_size > 0 ? `${(b.active / b.cohort_size) * 100}%` : '0%',
                          backgroundColor: 'var(--accent)',
                        }} />
                      </div>
                    </div>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', minWidth: 44, textAlign: 'right' }}>
                      {b.active}/{b.cohort_size}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ── Per-user table ──────────────────────────────────────────────────────────────

function UserRow({ u }: { u: UserActivityRow }) {
  const { tr } = useLocale()
  const t = tr.adminActivity
  const classicOnly = u.learning_sessions_started === 0 && u.classic_sessions_started > 0

  const lastActive = u.last_active_at === null || u.days_since_active === null
    ? <span style={{ color: 'var(--text-secondary)' }}>{t.never}</span>
    : t.daysAgo(u.days_since_active)

  return (
    <tr style={{ borderBottom: '1px solid var(--border)' }}>
      <td style={{ ...tdStyle, color: 'var(--text-primary)', fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {u.email}
      </td>
      <td style={tdStyle}>{new Date(u.registered_at).toISOString().split('T')[0]}</td>
      <td style={tdStyle}>{u.onboarding_completed ? t.yes : t.no}</td>
      <td style={tdStyle}>{lastActive}</td>
      <td style={tdStyle}>{u.streak_days}</td>
      <td style={tdStyle}>{t.sessionsFmt(u.sessions_completed, u.sessions_started)}</td>
      <td style={tdStyle}>
        {u.classic_sessions_started}
        {classicOnly && (
          <span style={chipStyle}>{t.classicOnlyNote}</span>
        )}
      </td>
      <td style={tdStyle}>{u.answers_submitted.toLocaleString()}</td>
      <td style={tdStyle}>{u.accuracy !== null ? `${(u.accuracy * 100).toFixed(0)}%` : '—'}</td>
      <td style={tdStyle}><MasteryLadder ladder={u.mastery_ladder} /></td>
      <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--text-primary)' }}>{u.mastered_count}</td>
    </tr>
  )
}

function UsersSection({ users }: { users: UserActivityRow[] }) {
  const { tr } = useLocale()
  const t = tr.adminActivity
  if (users.length === 0) {
    return <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{t.noUsers}</p>
  }
  const headers = [t.colUser, t.colRegistered, t.colOnboarded, t.colLastActive, t.colStreak, t.colSessions, t.colClassic, t.colAnswers, t.colAccuracy, t.colMastery, t.colMastered]
  return (
    <div style={{ ...tableWrapStyle, overflowX: 'auto' }}>
      <table style={{ ...tableStyle, minWidth: 900 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            {headers.map((h) => <th key={h} style={thStyle}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {users.map((u) => <UserRow key={u.user_id} u={u} />)}
        </tbody>
      </table>
    </div>
  )
}

// ── Worst-cards table ───────────────────────────────────────────────────────────

function WorstCardsSection({ cards }: { cards: WorstCard[] }) {
  const { tr } = useLocale()
  const t = tr.adminActivity
  if (cards.length === 0) {
    return <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{t.noWorstCards}</p>
  }
  return (
    <>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{t.worstHint}</p>
      <div style={{ ...tableWrapStyle, overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={thStyle}>{t.colTerm}</th>
              <th style={thStyle}>{t.colAttempts}</th>
              <th style={thStyle}>{t.colCardAccuracy}</th>
            </tr>
          </thead>
          <tbody>
            {cards.map((c, i) => (
              <tr key={c.card_id} style={{ borderBottom: i < cards.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <td style={{ ...tdStyle, color: 'var(--text-primary)', fontWeight: 500 }}>
                  {c.term ?? <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>—</span>}
                </td>
                <td style={tdStyle}>{c.correct}/{c.attempts}</td>
                <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--text-primary)' }}>{(c.accuracy * 100).toFixed(0)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ── Main page ───────────────────────────────────────────────────────────────────

export default function AdminActivityPage() {
  const { tr } = useLocale()
  const t = tr.adminActivity
  const { data, isLoading, isError } = useAdminActivity()

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>{t.title}</h1>
        <Link to="/admin" style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', textDecoration: 'none' }}>
          ← Admin Panel
        </Link>
      </div>
      <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '0 0 6px' }}>{t.subtitle}</p>
      {data && (
        <p style={{ color: 'var(--text-secondary)', fontSize: 12, margin: '0 0 36px' }}>
          {t.generatedAt(new Date(data.generated_at).toLocaleString())}
        </p>
      )}

      {isLoading && <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{t.loading}</p>}
      {isError && <p style={{ color: 'var(--danger)', fontSize: 14 }}>{t.failed}</p>}

      {data && (
        <>
          <section style={{ marginBottom: 48 }}>
            <h2 style={sectionHeadingStyle}>{t.cohortTitle}</h2>
            <CohortSection cohort={data.cohort} />
          </section>

          <section style={{ marginBottom: 48 }}>
            <h2 style={sectionHeadingStyle}>{t.perUserTitle}</h2>
            <UsersSection users={data.users} />
          </section>

          <section style={{ marginBottom: 48 }}>
            <h2 style={sectionHeadingStyle}>{t.worstTitle}</h2>
            <WorstCardsSection cards={data.worst_cards} />
          </section>
        </>
      )}
    </div>
  )
}

// ── Style constants ─────────────────────────────────────────────────────────────

const sectionHeadingStyle: CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  color: 'var(--text-primary)',
  margin: '0 0 20px',
  paddingBottom: 12,
  borderBottom: '1px solid var(--border)',
}

const subHeadingStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-secondary)',
  margin: '0 0 12px',
}

const tableWrapStyle: CSSProperties = {
  backgroundColor: 'var(--bg-surface)',
  borderRadius: 12,
  border: '1px solid var(--border)',
  overflow: 'hidden',
}

const tableStyle: CSSProperties = { width: '100%', borderCollapse: 'collapse' }

const thStyle: CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-secondary)',
  whiteSpace: 'nowrap',
}

const tdStyle: CSSProperties = {
  padding: '10px 14px',
  fontSize: 13,
  color: 'var(--text-secondary)',
  whiteSpace: 'nowrap',
}

const chipStyle: CSSProperties = {
  marginLeft: 8,
  fontSize: 10,
  fontWeight: 600,
  padding: '1px 7px',
  borderRadius: 'var(--radius-pill)',
  backgroundColor: 'var(--accent-soft)',
  color: 'var(--accent)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}
